'use client';

import {
  BROWSER_UPLOAD_PUT_TRANSPORT,
  browserUploadCleanupSignal,
  browserUploadPutHeaders,
  hashBrowserUploadBody,
  isStoredByEarlierPut,
  newBrowserUploadAttemptId,
  ownsBrowserUploadSession,
  parseBrowserUploadControl,
  postBrowserUploadControl,
  type BrowserUploadBegun,
  type BrowserUploadDirectControl,
  withDirectBrowserUploadControl,
} from './browser-upload-session-client';
import type { BrowserUploadIdentity } from './browser-upload-hash';
import {
  isProxyRejectionForDirectRetry,
  uploadProxyRequestUrl,
} from './upload-via-proxy';

/**
 * Upload a file via the upload proxy worker with progress tracking.
 *
 * @param signedUrl - The R2 signed upload URL
 * @param body - The file/blob to upload
 * @param contentType - The MIME type of the file
 * @param onProgress - Callback for upload progress (0 to 100)
 * @param options - Optional timeout/abort control
 * @returns The final status code and response body
 */
export async function uploadViaProxyWithProgress(
  signedUrl: string,
  body: Blob | File,
  contentType: string,
  onProgress: (progress: number) => void,
  options?: {
    timeoutMs?: number;
    signal?: AbortSignal;
    /** A browser-session identity already being hashed (e.g. in parallel with the presign call). */
    identity?: Promise<BrowserUploadIdentity>;
    /** Run session control on the caller's Convex client (skips the route hop). */
    directControl?: BrowserUploadDirectControl;
  }
): Promise<{ ok: boolean; status: number; statusText: string }> {
  const control = withDirectBrowserUploadControl(
    parseBrowserUploadControl(signedUrl),
    options?.directControl
  );
  if (control) {
    const controller = new AbortController();
    const onAbort = () => controller.abort();
    options?.signal?.addEventListener('abort', onAbort, { once: true });
    const attemptId = newBrowserUploadAttemptId();
    // Set only once this attempt's begin succeeded and made it the owner: a
    // failed begin, or a replay of another caller's session, never aborts.
    let ownsSession = false;
    const abortOwnedSession = async () => {
      if (!ownsSession) return;
      await postBrowserUploadControl(
        control,
        { operation: 'abort', attemptId },
        browserUploadCleanupSignal()
      ).catch(() => undefined);
    };
    try {
      const identity = await (options?.identity ?? hashBrowserUploadBody(body));
      const begun = await postBrowserUploadControl<BrowserUploadBegun>(
        control,
        {
          operation: 'begin',
          contentType,
          attemptId,
          ...identity,
          ...BROWSER_UPLOAD_PUT_TRANSPORT,
        },
        controller.signal
      );
      ownsSession = ownsBrowserUploadSession(begun);
      const part = await uploadPartWithProgress(
        begun.uploadUrl,
        body,
        contentType,
        onProgress,
        options,
        browserUploadPutHeaders(begun)
      );
      const stored = isStoredByEarlierPut(begun, part.status);
      if (!part.ok && !stored) {
        await abortOwnedSession();
        return part;
      }
      if (!part.etag && !begun.uploadHeaders)
        throw new Error('Upload part did not return an ETag.');
      await postBrowserUploadControl(
        control,
        {
          operation: 'complete',
          ...(part.etag ? { partEtag: part.etag } : {}),
          ...(begun.uploadHeaders ? BROWSER_UPLOAD_PUT_TRANSPORT : {}),
        },
        controller.signal
      );
      return { ok: true, status: 200, statusText: 'OK' };
    } catch (error) {
      await abortOwnedSession();
      throw error;
    } finally {
      options?.signal?.removeEventListener('abort', onAbort);
    }
  }
  const configuredProxyUrl = process.env.NEXT_PUBLIC_UPLOAD_PROXY_URL;
  if (!configuredProxyUrl) {
    return putWithProgress(signedUrl, body, contentType, onProgress, options);
  }
  const proxied = await putWithProgress(
    uploadProxyRequestUrl(configuredProxyUrl, signedUrl),
    body,
    contentType,
    onProgress,
    options
  );
  // Same fallback as uploadViaProxy: when the proxy cannot carry this signed
  // URL (worker down, or the bucket is outside the prefixes it allows), the
  // already-signed PUT goes straight to storage. Decided by the response.
  if (!isProxyRejectionForDirectRetry(proxied.status, proxied.responseText)) {
    return proxied;
  }
  onProgress(0);
  return putWithProgress(signedUrl, body, contentType, onProgress, options);
}

/** A browser-session part goes through the configured proxy only. */
async function uploadPartWithProgress(
  signedUrl: string,
  body: Blob | File,
  contentType: string,
  onProgress: (progress: number) => void,
  options: { timeoutMs?: number; signal?: AbortSignal } | undefined,
  signedHeaders: Record<string, string>
): Promise<UploadAttempt> {
  const proxyUrl = process.env.NEXT_PUBLIC_UPLOAD_PROXY_URL;
  return putWithProgress(
    proxyUrl ? uploadProxyRequestUrl(proxyUrl, signedUrl) : signedUrl,
    body,
    contentType,
    onProgress,
    options,
    signedHeaders
  );
}

type UploadAttempt = {
  ok: boolean;
  status: number;
  statusText: string;
  etag?: string;
  responseText: string;
};

function putWithProgress(
  targetUrl: string,
  body: Blob | File,
  contentType: string,
  onProgress: (progress: number) => void,
  options?: { timeoutMs?: number; signal?: AbortSignal },
  signedHeaders: Record<string, string> = {}
): Promise<UploadAttempt> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', targetUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    for (const [name, value] of Object.entries(signedHeaders))
      xhr.setRequestHeader(name, value);

    if (options?.timeoutMs) {
      xhr.timeout = options.timeoutMs;
    }

    if (options?.signal) {
      options.signal.addEventListener('abort', () => {
        xhr.abort();
        reject(new Error('Upload aborted'));
      });
    }

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percentComplete = Math.round((event.loaded / event.total) * 100);
        onProgress(percentComplete);
      }
    };

    xhr.onload = () => {
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        statusText: xhr.statusText,
        etag: xhr.getResponseHeader('etag') ?? undefined,
        responseText: xhr.responseText,
      });
    };

    xhr.onerror = () => {
      reject(new Error('Upload failed'));
    };

    xhr.ontimeout = () => {
      reject(new Error('Upload timed out'));
    };

    xhr.send(body);
  });
}

/**
 * `putSignedBrowserUpload` with byte progress (#4809): the same proxy route,
 * signed headers and timeout, sent with XHR so the composer chip can show
 * bytes sent. Resolves a Response carrying only the status.
 */
export function putSignedBrowserUploadWithProgress(
  signedUrl: string,
  body: Blob | File,
  contentType: string,
  signal: AbortSignal,
  signedHeaders: Record<string, string>,
  onLoaded: (loadedBytes: number, totalBytes: number) => void,
  timeoutMs = 10 * 60 * 1000
): Promise<Response> {
  const proxyUrl =
    process.env.NODE_ENV === 'development'
      ? '/api/upload-proxy'
      : process.env.NEXT_PUBLIC_UPLOAD_PROXY_URL;
  const target = proxyUrl
    ? uploadProxyRequestUrl(proxyUrl, signedUrl)
    : signedUrl;
  return new Promise((resolve, reject) => {
    if (signal.aborted) return reject(new Error('Upload aborted'));
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', target);
    xhr.setRequestHeader('Content-Type', contentType);
    for (const [name, value] of Object.entries(signedHeaders))
      xhr.setRequestHeader(name, value);
    xhr.timeout = timeoutMs;
    const onAbort = () => xhr.abort();
    signal.addEventListener('abort', onAbort, { once: true });
    const done = () => signal.removeEventListener('abort', onAbort);
    xhr.upload.onprogress = (event) => {
      onLoaded(event.loaded, event.lengthComputable ? event.total : body.size);
    };
    xhr.onload = () => {
      done();
      resolve(new Response(null, { status: xhr.status }));
    };
    xhr.onerror = () => {
      done();
      reject(new Error('Upload failed'));
    };
    xhr.onabort = () => {
      done();
      reject(new Error('Upload aborted'));
    };
    xhr.ontimeout = () => {
      done();
      reject(new Error('Upload timed out. Please try again.'));
    };
    xhr.send(body);
  });
}
